/**
 * Health bars — billboard sprites above fighters (alive fraction) and
 * buildings (hp fraction). Canvas textures redraw only when the fraction
 * changes by a visible step, so ~40 sprites stay cheap.
 */
import * as THREE from "three";
import type { Combatant } from "../sim/combat";
import type { PlacedBuilding } from "../sim/buildings";
import type { BuildingView } from "./buildings";

const W = 64;
const H = 8;

interface Bar {
  sprite: THREE.Sprite;
  canvas: HTMLCanvasElement;
  tex: THREE.CanvasTexture;
  lastFrac: number;
}

function makeBar(scene: THREE.Scene, color: string): Bar {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const tex = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }),
  );
  sprite.scale.set(7, 0.875, 1);
  sprite.renderOrder = 5;
  scene.add(sprite);
  const bar: Bar = { sprite, canvas, tex, lastFrac: -1 };
  drawBar(bar, 1, color);
  return bar;
}

function drawBar(bar: Bar, frac: number, color: string): void {
  const ctx = bar.canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "rgba(0,0,0,0.65)";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = color;
  ctx.fillRect(1, 1, (W - 2) * Math.max(0, Math.min(1, frac)), H - 2);
  bar.tex.needsUpdate = true;
  bar.lastFrac = frac;
}

export class HealthBars {
  private fighterBars = new Map<Combatant, Bar>();
  private buildingBars = new Map<PlacedBuilding, Bar>();

  constructor(private readonly scene: THREE.Scene) {}

  private barColor(team: 0 | 1): string {
    return team === 0 ? "#7fae6a" : "#c96a5a";
  }

  /**
   * @param isVisible optional fog-of-war filter: enemy (team 1) fighters for
   * which it returns false get their bar hidden. Omit for no filtering.
   */
  syncFighters(
    fighters: Combatant[],
    groundY: (x: number, z: number) => number,
    isVisible?: (f: Combatant) => boolean,
  ): void {
    const alive = new Set(fighters);
    for (const [c, bar] of this.fighterBars) {
      if (!alive.has(c)) {
        this.scene.remove(bar.sprite);
        bar.tex.dispose();
        this.fighterBars.delete(c);
      }
    }
    for (const c of fighters) {
      let bar = this.fighterBars.get(c);
      if (!bar) {
        bar = makeBar(this.scene, this.barColor(c.team));
        this.fighterBars.set(c, bar);
      }
      const shown = !isVisible || c.team === 0 || isVisible(c);
      bar.sprite.visible = shown;
      if (!shown) continue;
      const frac = c.alive / Math.max(1, c.maxAlive);
      if (Math.abs(frac - bar.lastFrac) > 0.02) {
        drawBar(bar, frac, this.barColor(c.team));
      }
      const a = c.view.anchor;
      bar.sprite.position.set(a.x, groundY(a.x, a.z) + 5.2, a.z);
    }
  }

  /**
   * @param isVisible optional fog-of-war filter: enemy (team 1) buildings for
   * which it returns false get their bar hidden. Omit for no filtering.
   */
  syncBuildings(
    views: BuildingView[],
    groundY: (x: number, z: number) => number,
    isVisible?: (v: BuildingView) => boolean,
  ): void {
    const alive = new Set(views.map((v) => v.building));
    for (const [b, bar] of this.buildingBars) {
      if (!alive.has(b)) {
        this.scene.remove(bar.sprite);
        bar.tex.dispose();
        this.buildingBars.delete(b);
      }
    }
    for (const v of views) {
      const b = v.building;
      const shown = !isVisible || b.team === 0 || isVisible(v);
      if (b.state !== "active" && b.hp >= b.maxHp) {
        const stale = this.buildingBars.get(b);
        if (stale) stale.sprite.visible = false;
        continue;
      }
      let bar = this.buildingBars.get(b);
      if (!bar) {
        bar = makeBar(this.scene, this.barColor(b.team));
        this.buildingBars.set(b, bar);
      }
      bar.sprite.visible = shown;
      if (!shown) continue;
      const frac = b.state === "active" ? b.hp / b.maxHp : b.progress;
      if (Math.abs(frac - bar.lastFrac) > 0.02) {
        drawBar(bar, frac, this.barColor(b.team));
      }
      bar.sprite.position.set(b.x, groundY(b.x, b.z) + 9, b.z);
    }
  }
}
